import React, { useEffect, useRef, useCallback } from 'react';
import { EditorView, basicSetup } from 'codemirror';
import { css } from '@codemirror/lang-css';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';

// Simple CSS linter that checks for common issues
const cssLinter = linter((view) => {
  const diagnostics = [];
  const content = view.state.doc.toString();

  // Check for forbidden patterns
  const forbiddenPatterns = [
    { pattern: /url\s*\(/gi, message: 'url() function is not allowed for security reasons' },
    { pattern: /@import/gi, message: '@import statements are not allowed' },
    { pattern: /expression\s*\(/gi, message: 'expression() is not allowed' },
    { pattern: /javascript\s*:/gi, message: 'javascript: protocol is not allowed' },
  ];

  forbiddenPatterns.forEach(({ pattern, message }) => {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(content)) !== null) {
      diagnostics.push({
        from: match.index,
        to: match.index + match[0].length,
        severity: 'error',
        message: message,
      });
    }
  });

  return diagnostics;
});

const CssEditor = ({ initialValue, onChange, onValidationChange, height = '400px' }) => {
  const editorRef = useRef(null);
  const viewRef = useRef(null);

  const handleUpdate = useCallback((update) => {
    if (update.docChanged) {
      const newValue = update.state.doc.toString();
      if (onChange) {
        onChange(newValue);
      }
    }
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    // Create editor state
    const state = EditorState.create({
      doc: initialValue || '',
      extensions: [
        basicSetup,
        css(),
        lintGutter(),
        cssLinter,
        EditorView.updateListener.of(handleUpdate),
        EditorView.theme({
          '&': { height: height },
          '.cm-scroller': { overflow: 'auto' },
          '.cm-content': { fontFamily: 'monospace' },
        }),
      ],
    });

    // Create editor view
    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
    };
  }, []);

  // Update content when initialValue changes externally (e.g., reset to default)
  useEffect(() => {
    if (viewRef.current && initialValue !== undefined) {
      const currentValue = viewRef.current.state.doc.toString();
      if (currentValue !== initialValue) {
        viewRef.current.dispatch({
          changes: {
            from: 0,
            to: currentValue.length,
            insert: initialValue,
          },
        });
      }
    }
  }, [initialValue]);

  return (
    <div
      ref={editorRef}
      style={{
        border: '1px solid #ddd',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    />
  );
};

export default CssEditor;
